import React from "react";
import Body, { ExtendedBodyPart } from "./BodyHighlighter";
import {
  DISTRICT_TO_SLUGS,
  SLUG_TO_DISTRICT,
  getMuscleColor,
  getMuscleColorUnique,
} from "./muscleConfig";

type BodyMuscleMapProps = {
  activeDistricts: string[];
  dimDistricts?: string[];
  dimColor?: string;
  hoveredDistrict: string | null;
  onHoverDistrict: (district: string | null) => void;
  onClickDistrict?: (district: string) => void;
  gender?: "male" | "female";
  scale?: number;
  uniqueColors?: boolean;
  uniformColor?: string;
  colorFn?: (district: string) => string;
};

const buildBodyData = (
  activeDistricts: string[],
  hoveredDistrict: string | null,
  colorFn: (district: string) => string = getMuscleColor,
  fullOpacity: boolean = false
): ExtendedBodyPart[] => {
  const parts: ExtendedBodyPart[] = [];

  for (const district of activeDistricts) {
    const slugs = DISTRICT_TO_SLUGS[district];
    if (!slugs) continue;

    const isHovered = hoveredDistrict === district;
    const color = colorFn(district);

    for (const slug of slugs) {
      parts.push({
        slug,
        color,
        intensity: fullOpacity ? 2 : (isHovered ? 2 : 1),
      });
    }
  }

  return parts;
};

export const BodyMuscleMap: React.FC<BodyMuscleMapProps> = ({
  activeDistricts,
  dimDistricts,
  dimColor = "#4B5563",
  hoveredDistrict,
  onHoverDistrict,
  onClickDistrict,
  gender = "male",
  scale = 1.5,
  uniqueColors = false,
  uniformColor,
  colorFn: colorFnProp,
}) => {
  const colorFn = colorFnProp
    ? colorFnProp
    : uniformColor
    ? () => uniformColor
    : uniqueColors
    ? getMuscleColorUnique
    : getMuscleColor;
  const data = [
    ...buildBodyData(activeDistricts, hoveredDistrict, colorFn, uniqueColors || !!uniformColor),
    ...buildBodyData(dimDistricts || [], null, () => dimColor, false),
  ];

  const handleClick = (part: ExtendedBodyPart) => {
    if (part.slug) {
      const district = SLUG_TO_DISTRICT[part.slug];
      if (district) {
        if (onClickDistrict) {
          onClickDistrict(district);
        } else {
          onHoverDistrict(district);
        }
      }
    }
  };

  return (
    <div className="flex flex-col items-center w-full">
      <div className="flex gap-2 w-full justify-center" style={{ maxWidth: "100%" }}>
        <div className="flex flex-col items-center flex-1" style={{ maxWidth: "50%" }}>
          <div className="w-full [&>svg]:w-full [&>svg]:h-auto">
            <Body
              data={data}
              side="front"
              gender={gender}
              scale={scale}
              border="none"
              onBodyPartClick={handleClick}
            />
          </div>
          <span className="text-[10px] text-gray-400 -mt-1">FRONT</span>
        </div>

        <div className="flex flex-col items-center flex-1" style={{ maxWidth: "50%" }}>
          <div className="w-full [&>svg]:w-full [&>svg]:h-auto">
            <Body
              data={data}
              side="back"
              gender={gender}
              scale={scale}
              border="none"
              onBodyPartClick={handleClick}
            />
          </div>
          <span className="text-[10px] text-gray-400 -mt-1">BACK</span>
        </div>
      </div>
    </div>
  );
};
